// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const CrossRepoChatView = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("Thinking...");
  const inputRef = useRef<any>(null);
  const messagesEndRef = useRef<any>(null);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);
  useEffect(() => { if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);
    const chatMessages = [...messages.map((m: any) => ({ role: m.role, content: m.text })), { role: "user", content: userMsg }];
    const res = await apiCall("/extraction/chat/cross-repo", { method: "POST", body: JSON.stringify({ messages: chatMessages }) });
    if (res?.response) {
      setMessages(prev => [...prev, { role: "assistant", text: res.response, provider: res.provider, sources: res.sources }]);
    } else {
      setMessages(prev => [...prev, { role: "assistant", text: "Error: Could not get a response. Please try again." }]);
    }
    setLoading(false);
  };

  const suggestions = [
    "What governing laws are used across all contracts?",
    "Which contracts are expiring in the next 6 months?",
    "Compare the notice periods across all MSAs",
    "Which contracts have auto-renewal clauses?",
    "Summarize the indemnification terms across the repository",
    "Which counterparties have multiple contracts?",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
      <SectionTitle sub="Ask questions across your entire contract repository">AI Contract Chat</SectionTitle>
      <div style={{ flex: 1, overflow: "auto", padding: "0 4px 16px", marginBottom: 12 }}>
        {messages.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>◆</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.tx, marginBottom: 6 }}>Cross-Repository AI Chat</div>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 24, maxWidth: 460, margin: "0 auto 24px" }}>Ask questions about all contracts in your repository. AI will analyze metadata and extracted terms to provide comprehensive answers with contract citations.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, maxWidth: 560, margin: "0 auto" }}>
              {suggestions.map((s: any, i: number) => (
                <Card key={i} onClick={() => { setInput(s); if (inputRef.current) inputRef.current.focus(); }} style={{ padding: 12, cursor: "pointer", textAlign: "left" }}>
                  <div style={{ fontSize: 12, color: T.tx, lineHeight: 1.4 }}>{s}</div>
                </Card>
              ))}
            </div>
          </div>
        )}
        {messages.map((m: any, i: number) => (
          <div key={i} style={{ marginBottom: 14, display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "80%", padding: "12px 16px", borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", backgroundColor: m.role === "user" ? T.acc : T.bg3, color: m.role === "user" ? "#fff" : T.tx, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {m.role === "assistant" ? <>{renderMarkdown(m.text)}{m.provider === 'harvey' && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '2px 8px', borderRadius: 10, backgroundColor: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.15)', fontSize: 10, color: 'rgb(99,102,241)' }}>Harvey AI</div>}{m.sources && m.sources.length > 0 && <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${T.brd}` }}><div style={{ fontSize: 9, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: 'uppercase' }}>Sources</div>{m.sources.map((s: any, si: number) => <div key={si} style={{ fontSize: 10, color: T.txM, padding: '2px 6px', backgroundColor: T.bg3, borderRadius: 3, marginBottom: 2 }}><strong>[{s.citation_num || si+1}]</strong> {s.document_name || s.title || 'Source'}{s.page ? `, p.${s.page}` : ''}</div>)}</div>}</> : m.text}
            </div>
            {m.role === "assistant" && <ChatExportButtons content={m.text} />}
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 14 }}>
            <div style={{ padding: "12px 16px", borderRadius: "12px 12px 12px 2px", backgroundColor: T.bg3, color: T.acc, fontSize: 13, fontWeight: 500 }}>Analyzing repository...</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div style={{ display: "flex", gap: 8, padding: "0 0 4px" }}>
        <VoiceInput onTranscript={(text: string) => setInput(text)} disabled={loading} />
        <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()} placeholder="Ask about your contracts..." style={{ flex: 1, padding: "12px 14px", borderRadius: 8, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 13, outline: "none", fontFamily: "inherit" }} />
        <Btn v="primary" onClick={() => { send(); }} disabled={loading || !input.trim()}>Send</Btn>
      </div>
    </div>
  );
};

/* ═══════════════════ SELF-SERVICE ═══════════════════ */
